import React, { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../../contexts/AuthContext'
import { AuthService } from '../../../services/AuthService'

interface AuthGuardProps {
  children: ReactNode
  requireAuth?: boolean
  redirectTo?: string
  isLoading?: boolean
  isInitialized?: boolean
  isAuthenticated?: boolean
}

export const AuthGuard: React.FC<AuthGuardProps> = ({ 
  children, 
  requireAuth = true,
  isInitialized: isInitializedProp,
  isAuthenticated: isAuthenticatedProp,
  isLoading: isLoadingProp,
  redirectTo = '/' 
}) => {
  const location = useLocation()
  const auth = useAuth()

  const isInitialized = isInitializedProp ?? auth.isInitialized
  const isAuthenticated = isAuthenticatedProp ?? auth.isAuthenticated
  const isLoading = isLoadingProp ?? auth.isLoading

  // Show loading while initializing
  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  // Redirect based on auth requirement
  if (requireAuth && !isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />
  }

  if(!requireAuth){
    return <>{children}</>
  }

  return <>{children}</>
}

interface AdminGuardProps {
  children: ReactNode
  redirectTo?: string
}

export const AdminGuard: React.FC<AdminGuardProps> = ({ children, redirectTo = '/' }) => {
  const { isInitialized, isAuthenticated, isLoading, profile } = useAuth()

  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  if (!isAuthenticated || !AuthService.isAdministrator(profile)) {
    return <Navigate to={redirectTo} replace />
  }

  return <>{children}</>
}